import { getId, getFlagValue } from "./context";

// Re-export context functions for convenience
export {
  setContext,
  setFlag,
  clearContext,
  getId,
  getFlags,
  getFlagValue,
  type FeatureFlagValue,
  type FeatureFlagsConfig,
} from "./context";

/**
 * FNV-1a hash function for stable user bucketing
 * Fast, excellent distribution, industry-proven (used by Redis, Unleash, etc.)
 * @see https://en.wikipedia.org/wiki/Fowler%E2%80%93Noll%E2%80%93Vo_hash_function
 */
function hashFNV1a(str: string): number {
  const FNV_OFFSET_BASIS = 2166136261;
  const FNV_PRIME = 16777619;

  let hash = FNV_OFFSET_BASIS;

  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, FNV_PRIME);
  }

  // Convert to unsigned 32-bit integer
  return hash >>> 0;
}

/**
 * Determines if a user should be included in a percentage rollout
 * Uses consistent hash-based bucketing to ensure same user gets same result even across worker and client contexts
 * @param flagName - Name of the feature flag
 * @param id - User's session ID for stable bucketing
 * @param percentage - Rollout percentage (0-100)
 * @returns true if user is in the rollout bucket
 */
function getStableRollout(
  flagName: string,
  id: string,
  percentage: number
): boolean {
  const hash = hashFNV1a(`${id}:${flagName}`);
  const bucket = hash % 100; // Maps to 0-99
  return bucket < percentage;
}

/**
 * Check for feature flag override in query string, sessionStorage, or localStorage
 * Checks in priority order: query string > sessionStorage > localStorage
 * @param flagName - Name of the feature flag
 * @returns Override value (true/false) or undefined if no override exists
 */
function getOverride(flagName: string): boolean | undefined {
  const key = `amb-flag-${flagName}`;

  // 1. Check query string (highest priority)
  if (typeof window !== "undefined" && window.location) {
    const params = new URLSearchParams(window.location.search);
    const queryValue = params.get(key);
    if (queryValue !== null) {
      sessionStorage.setItem(key, queryValue);
      return queryValue === "true" || queryValue === "1";
    }
  }

  // 2. Check sessionStorage
  try {
    const sessionValue = sessionStorage.getItem(key);
    if (sessionValue !== null) {
      return sessionValue === "true" || sessionValue === "1";
    }
  } catch (e) {
    // sessionStorage might not be available in some contexts
  }

  // 3. Check localStorage
  try {
    const localValue = localStorage.getItem(key);
    if (localValue !== null) {
      return localValue === "true" || localValue === "1";
    }
  } catch (e) {
    // localStorage might not be available in some contexts
  }

  return undefined;
}

/**
 * Check if a feature flag is enabled for the current user
 * Flags are resolved from context initialized during bootstrap
 *
 * @param flagName - Name of the feature flag to check
 * @returns true if the feature is enabled, false otherwise
 *
 * @example
 * // Boolean flag or percentage rollout flag usage
 * if (isEnabled('new_feature')) { ... }
 *
 * // Local testing overrides (checked in priority order):
 * // Query string: ?amb-flag-new-feature=true
 * // sessionStorage: sessionStorage.setItem('amb-flag-new-feature', 'true')
 * // localStorage: localStorage.setItem('amb-flag-new-feature', 'true')
 *
 * @example
 * // Worker usage (context is automatically initialized)
 * if (isEnabled('new_feature')) { ... }
 */
export function isEnabled(flagName: string): boolean {
  // Check for overrides first (for local testing/debugging)
  const override = getOverride(flagName);
  if (override !== undefined) {
    return override;
  }

  const flagValue = getFlagValue(flagName);

  // Default to false if flag not defined (safe default)
  if (flagValue === undefined) {
    return false;
  }

  // Boolean flags - simple on/off
  if (typeof flagValue === "boolean") {
    return flagValue;
  }

  // Percentage rollout (0-100)
  if (typeof flagValue === "number") {
    // Get session ID from analytics instance
    const id = getId();

    if (!id) {
      // Session not ready yet - default to false for safety
      // This ensures features are opt-in rather than accidentally enabled
      return false;
    }

    return getStableRollout(flagName, id, flagValue);
  }

  // Unknown type - default to false
  return false;
}
